
import { ReactNode, useEffect, useState } from 'react';
import { AuthContext } from './AuthContext';
import { AuthResponse, UserInfoResponse } from '@/types/auth';
import { storage } from '@/services/storageService';
import { saveRememberedLoginProfile } from '@/services/rememberedLoginProfile';
import { User, Organization, OrganizationMemberResponse, OrganizationWithMembership } from '@opp/shared';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [doctor, setDoctor] = useState<User | null>(null);
  const [organizations, setOrganizations] = useState<Organization[] | null>(
    null
  );
  const [activeOrganization, setActiveOrganizationState] =
    useState<OrganizationMemberResponse | null>(null);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // Recupera os dados do localStorage ao iniciar
    const storedToken = storage.get<string>('token');
    const storedUser = storage.get<{
      user: User;
      organizations: Organization[];
    }>('user');
    const storedActiveOrg = storage.get<OrganizationMemberResponse>('activeOrganization');

    // Sem key de user logado: limpa storage e volta ao login
    if (storedToken && !storedUser?.user) {
      storage.clear();
      window.location.href = '/auth';
      return;
    }

    if (storedToken && storedUser) {
      setToken(storedToken);
      setDoctor(storedUser.user);
      setOrganizations(storedUser.organizations);
    }

    if (storedActiveOrg) {
      setActiveOrganizationState(storedActiveOrg);
      setIsOwner(storedActiveOrg.isOwner);
    }
  }, []);

  const signIn = async (data: AuthResponse) => {
    const { token } = data;

    // Salva no localStorage
    storage.set('token', token);

    // Atualiza o estado
    setToken(token);
  };

  const setUserInfo = async (data: UserInfoResponse) => {
    const { user, organizations } = data;

    storage.set('user', { user, organizations });
    setDoctor(user);
    setOrganizations(organizations);
    saveRememberedLoginProfile(user);
  };

  const signOut = () => {
    // Remove do localStorage
    storage.remove('token');
    storage.remove('user');
    storage.remove('activeOrganization');

    // Limpa o estado
    setToken(null);
    setDoctor(null);
  };

  const updateDoctor = (updatedDoctor: User) => {
    const storedUser = storage.get<{
      user: User;
      organizations: Organization[];
    }>('user');

    if (!storedUser) return;

    // Atualiza no localStorage
    storage.set('user', {
      user: updatedDoctor,
      organizations: storedUser.organizations,
    });

    // Atualiza o estado
    setDoctor(updatedDoctor);
    saveRememberedLoginProfile(updatedDoctor);
  };

  const setActiveOrganization = async (org: OrganizationWithMembership | null, userId?: string) => {
    const user = doctor?.id ?? userId;

    if (!org) {
      setActiveOrganizationState(null);
      setIsOwner(false);
      storage.remove('activeOrganization');
      return;
    }

    const membership =
      org.Membership?.find((m) => m.userId === user) ?? org.Membership?.[0];

    // Não permite ativar equipe com membership pendente
    if (membership?.status === 'PENDING') {
      return;
    }

    const newOrg = {
      ...org,
      Membership: undefined,
      isOwner: org.Membership?.some((m) => m.userId === user && m.isOwner) ?? false,
      status: membership?.status ?? 'ACCEPTED',
    } as OrganizationMemberResponse;

    setActiveOrganizationState(newOrg);
    setIsOwner(newOrg.isOwner);
    storage.set('activeOrganization', newOrg);
  };

  return (
    <AuthContext.Provider
      value={{
        doctor,
        token,
        organizations,
        activeOrganization,
        isOwner,
        setActiveOrganization,
        isAuthenticated: !!doctor,
        signIn,
        setUserInfo,
        signOut,
        updateDoctor,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
